// Las tres secciones de la sidebar —"Redes sociales", "Catálogo" y "Herramientas"— con su
// panel lateral (ADR-2026-10-05-la-sidebar-por-secciones).
//
// Reemplazan a los grupos "Imagen" / "Video" con sus "Generar…" y los dos modos más usados
// (ADR-2026-09-19-la-sidebar-muestra-los-dos-modos-mas-usados): todos los modos no entraban,
// y la home ya parte la app por destino. Tocar una sección abre, a la derecha de la
// sidebar y a la altura de la fila, un panel con sus modos. Se abre con CLICK (no hover: un
// menú por hover se cierra cuando el mouse cruza en diagonal hacia él) y se cierra al
// elegir, tocando afuera, con Esc o tocando la misma sección; tocar otra cambia el
// contenido sin cerrarlo.
//
// Va por portal a `body`: el `<aside>` tiene `overflow-hidden` y lo recortaría, y tiene que
// quedar encima de la columna de resultados de los modos (z-[100]).

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { LucideIcon } from 'lucide-react';
import type { HerramientaAtlas } from './contenidoAtlas';
import type { ModoHome } from './EstiloMinimal';
import { CardModoProximo } from './CardModoProximo';
import {
  CARATULAS_CON_TINTE,
  MODOS_DE_CATALOGO,
  MODOS_DE_HERRAMIENTAS,
  MODOS_DE_REDES,
  PROXIMOS_DE_CATALOGO,
} from './seccionesDeModos';
import { GrillaDeModos } from '../v2/components/GrillaDeModos';
import { bajadaCortaModo, nombreCortoModo, portadaDeRubro } from '../v2/modesCatalog';

type IdSeccion = 'redes' | 'catalogo' | 'herramientas';

/** Una edición rápida tal como la exporta `EDICIONES_RAPIDAS`. */
export interface EdicionRapida {
  label: string;
  desc: string;
  Icono: LucideIcon;
  ruta: string;
  kindParaCandado?: string;
}

/** Una fila del panel, ya resuelta. */
interface Fila {
  id: string;
  nombre: string;
  bajada: string;
  ruta: string;
  bloqueada: boolean;
  /** El póster del modo, de respaldo para la carátula (Redes, Catálogo). */
  imagen?: string;
  /** El ícono (Herramientas). */
  icono?: ReactNode;
}

const SECCIONES: { id: IdSeccion; nombre: string; icono: string }[] = [
  { id: 'redes', nombre: 'Redes sociales', icono: 'share' },
  { id: 'catalogo', nombre: 'Catálogo', icono: 'storefront' },
  { id: 'herramientas', nombre: 'Herramientas', icono: 'handyman' },
];

const estaEn = (pathname: string, ruta: string) => pathname === ruta || pathname.startsWith(`${ruta}/`);

/** Dos cards de la home (o dos filas de herramientas) por renglón. */
const ANCHO_PANEL = 540;
const MARGEN = 12;

export function SeccionesDeLaSidebar({
  herramientas,
  ediciones,
  estaBloqueada,
  edicionBloqueada,
  rubroMarca,
  pathname,
  colapsada,
  irA,
  alBloqueada,
  claseFila,
  propsTooltip,
  alAbrir,
}: {
  /** Los modos que este usuario ve (rubro, flags y dev ya filtrados). */
  herramientas: readonly HerramientaAtlas[];
  /** Las ediciones rápidas visibles (Expandir ya filtrado por su flag). */
  ediciones: readonly EdicionRapida[];
  /** El candado por plan de un modo, por su `path` (el mismo criterio de siempre). */
  estaBloqueada: (path: string) => boolean;
  /** El candado de una edición rápida (`candadoEdicionRapida`). */
  edicionBloqueada: (e: EdicionRapida) => boolean;
  rubroMarca: string | null;
  pathname: string;
  colapsada: boolean;
  irA: (ruta: string) => void;
  /** Una fila con candado no navega: abre los planes. */
  alBloqueada: () => void;
  /** Las clases de una fila de la sidebar (activa o no), las mismas que el resto de la nav. */
  claseFila: (activa: boolean) => string;
  propsTooltip: (texto: string) => Record<string, unknown>;
  /** Al abrir un panel (cierra la card de ayuda de la sidebar). */
  alAbrir?: () => void;
}) {
  const [abierta, setAbierta] = useState<{ id: IdSeccion; fila: HTMLElement } | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [top, setTop] = useState(0);

  const porId = new Map(herramientas.map((h) => [h.id, h]));
  const filaDeModo = (path: string, conImagen: boolean): Fila | null => {
    const h = porId.get(path);
    if (!h) return null;
    return {
      id: h.id,
      // Esta sidebar es de la interfaz nueva: "Recrear", no "Plantillas".
      nombre: nombreCortoModo(h.id, h.nombre, { uiNueva: true }),
      bajada: bajadaCortaModo(h.id, h.descripcion),
      ruta: h.ruta,
      bloqueada: estaBloqueada(h.id),
      ...(conImagen
        ? { imagen: h.poster }
        : { icono: <span className="material-symbols-rounded text-[20px]">{h.icono}</span> }),
    };
  };
  const de = (paths: readonly string[], conImagen: boolean) =>
    paths.flatMap((p) => {
      const f = filaDeModo(p, conImagen);
      return f ? [f] : [];
    });

  const filas: Record<IdSeccion, Fila[]> = {
    redes: de(MODOS_DE_REDES, true),
    catalogo: de(MODOS_DE_CATALOGO, true),
    herramientas: [
      ...ediciones.map((e) => ({
        id: e.ruta,
        nombre: e.label,
        bajada: e.desc,
        ruta: e.ruta,
        bloqueada: edicionBloqueada(e),
        icono: <e.Icono className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden="true" />,
      })),
      ...de(MODOS_DE_HERRAMIENTAS, false),
    ],
  };

  // Una sección sin filas para este usuario (p. ej. un rubro sin nada de catálogo) no se
  // dibuja: abriría un panel vacío.
  const visibles = SECCIONES.filter((s) => filas[s.id].length > 0 || (s.id === 'catalogo' && PROXIMOS_DE_CATALOGO.length > 0));

  // Cerrar: al cambiar de pantalla y al colapsar o expandir la sidebar (la fila se mueve).
  useEffect(() => setAbierta(null), [pathname, colapsada]);

  useEffect(() => {
    if (!abierta) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierta(null);
    };
    const alTocar = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t)) return;
      // Las filas de sección manejan su propio click (alternar / cambiar).
      if ((t as HTMLElement).closest?.('[data-seccion-sidebar]')) return;
      setAbierta(null);
    };
    document.addEventListener('keydown', alTeclear);
    document.addEventListener('mousedown', alTocar);
    return () => {
      document.removeEventListener('keydown', alTeclear);
      document.removeEventListener('mousedown', alTocar);
    };
  }, [abierta]);

  // A la altura de la fila, pero sin salirse de la ventana por abajo.
  useLayoutEffect(() => {
    if (!abierta) return;
    const fila = abierta.fila.getBoundingClientRect();
    const alto = panelRef.current?.offsetHeight ?? 0;
    setTop(Math.max(MARGEN, Math.min(fila.top - 8, window.innerHeight - alto - MARGEN)));
  }, [abierta]);

  const izquierda = abierta ? (abierta.fila.closest('aside')?.getBoundingClientRect().right ?? abierta.fila.getBoundingClientRect().right) + 8 : 0;

  const elegir = (f: Fila) => {
    setAbierta(null);
    if (f.bloqueada) alBloqueada();
    else irA(f.ruta);
  };

  const seccionAbierta = abierta ? SECCIONES.find((s) => s.id === abierta.id) : null;

  return (
    <>
      {visibles.map((s) => {
        const activa = filas[s.id].some((f) => estaEn(pathname, f.ruta));
        const estaAbierta = abierta?.id === s.id;
        return (
          <button
            key={s.id}
            type="button"
            data-seccion-sidebar=""
            aria-expanded={estaAbierta}
            aria-haspopup="menu"
            onClick={(e) => {
              const fila = e.currentTarget;
              alAbrir?.();
              setAbierta((a) => (a?.id === s.id ? null : { id: s.id, fila }));
            }}
            {...propsTooltip(s.nombre)}
            className={`${claseFila(activa)} ${estaAbierta && !activa ? '!bg-black/[0.04]' : ''}`}
          >
            <span className="material-symbols-rounded shrink-0 text-[19px]">{s.icono}</span>
            {!colapsada && (
              <>
                <span className="min-w-0 flex-1 truncate">{s.nombre}</span>
                <span
                  aria-hidden="true"
                  className={`material-symbols-rounded shrink-0 text-[18px] text-black/30 transition-transform duration-200 ${estaAbierta ? 'translate-x-0.5 text-black/50' : ''}`}
                >
                  chevron_right
                </span>
              </>
            )}
          </button>
        );
      })}

      {abierta &&
        seccionAbierta &&
        createPortal(
          <div
            ref={panelRef}
            role="menu"
            aria-label={seccionAbierta.nombre}
            style={{ top, left: izquierda, width: ANCHO_PANEL, maxHeight: `calc(100vh - ${MARGEN * 2}px)` }}
            className="animate-fade-in fixed z-[125] overflow-y-auto rounded-2xl bg-white p-2 shadow-[0_24px_60px_-12px_rgba(16,24,45,0.28)] ring-1 ring-black/[0.06]"
          >
            <p className="px-2.5 pb-1.5 pt-2 text-[10.5px] font-semibold uppercase leading-none tracking-[0.09em] text-black/35">
              {seccionAbierta.nombre}
            </p>
            {abierta.id === 'herramientas' ? (
              // Herramientas: dos columnas de filas (ícono, nombre y bajada).
              <div className="grid grid-cols-2 gap-x-1 gap-y-0.5">
                {filas.herramientas.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    role="menuitem"
                    onClick={() => elegir(f)}
                    className={`group flex w-full min-w-0 items-center gap-3 rounded-xl p-2 text-left transition-colors hover:bg-black/[0.04] ${
                      estaEn(pathname, f.ruta) ? 'bg-black/[0.03]' : ''
                    }`}
                  >
                    <span className="relative grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-[10px] bg-[#F2F3F7] text-black/60">
                      {f.icono}
                      {f.bloqueada && (
                        <span aria-hidden="true" className="absolute inset-0 grid place-items-center bg-black/45 text-white">
                          <span className="material-symbols-rounded text-[16px]">lock</span>
                        </span>
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className={`block truncate text-[13.5px] font-semibold ${f.bloqueada ? 'text-black/45' : 'text-brand-black/90'}`}>
                        {f.nombre}
                      </span>
                      <span className="mt-0.5 block truncate text-[12px] leading-snug text-black/50">
                        {f.bloqueada ? 'Incluido en los planes pagos' : f.bajada}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              // Redes sociales y Catálogo: las MISMAS cards del popup de la home (carátula
              // 3:2 con el fondo de la sección, nombre y bajada), en dos columnas
              // (ADR-2026-10-05-los-paneles-de-la-sidebar-con-las-cards-de-la-home).
              <GrillaDeModos
                uiNueva
                modos={filas[abierta.id].map(
                  (f): ModoHome => ({
                    path: f.id,
                    titulo: f.nombre,
                    imagen: f.imagen ?? '',
                    bloqueado: f.bloqueada,
                    href: f.bloqueada ? undefined : f.ruta,
                    onAbrir: () => elegir(f),
                  }),
                )}
                rubroMarca={rubroMarca}
                onAbrir={(m) => m.onAbrir()}
                className="grid grid-cols-2 gap-3 p-1.5"
                caratulaPropia={(path) =>
                  portadaDeRubro(path, rubroMarca) ? undefined : CARATULAS_CON_TINTE[abierta.id === 'redes' ? 'redes' : 'catalogo'][path]
                }
                alFinal={
                  abierta.id === 'catalogo'
                    ? PROXIMOS_DE_CATALOGO.map((p) => <CardModoProximo key={p.nombre} nombre={p.nombre} bajada={p.bajada} />)
                    : undefined
                }
              />
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
